import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch, authHeaders, authHeadersFormData } from './apiHelpers';
import { useCountry } from '../context/CountryContext';

export interface SiteInfo {
    uniqueSiteId: number;
    siteName: string;
    brand: string;
    streetAddress: string;
    postalCode: string;
    town: string;
    telephoneExchange: string;
    telephoneAlternative: string;
    contact: string;
    openingHours: string;
    lastUpdatedBy: string;
    updateDateUTC: Date;
    customerNumber: string;
    published: boolean;
}

export interface SiteInfoProp {
    column: string;
    value: string;
}

export function useSearchSites(query: string | null, filter: string) {
    const { country } = useCountry();
    return useQuery<SiteInfo[], Error>({
        queryKey: ['sites', 'search', query, filter, country],
        queryFn: () => apiFetch<SiteInfo[]>(`api/SiteInfo/SearchSites?query=${encodeURIComponent(query ?? '')}&filter=${encodeURIComponent(filter)}&market=${encodeURIComponent(country)}`, {
            method: 'GET',
            headers: authHeaders()
        }),
        enabled: query !== null
    });
}

export function useSiteInfo(siteId: number) {
    return useQuery<SiteInfo, Error>({
        queryKey: ['sites', siteId],
        queryFn: () => apiFetch<SiteInfo>(`api/SiteInfo/GetSiteInfo?siteId=${siteId}`, {
            method: 'GET',
            headers: authHeaders()
        }),
        enabled: siteId > 0
    });
}

export function useAddSiteInfo() {
    const queryClient = useQueryClient();
    return useMutation<number, Error, FormData>({
        mutationFn: (data) => apiFetch<number>('api/SiteInfo/AddSiteInfo', {
            method: 'POST',
            body: data,
            headers: authHeadersFormData()
        }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sites'] })
    });
}

export function useEditSiteInfo() {
    const queryClient = useQueryClient();
    return useMutation<void, Error, { siteId: number; data: FormData }>({
        mutationFn: ({ siteId, data }) => apiFetch<void>(`api/SiteInfo/EditSiteInfo?siteId=${siteId}`, {
            method: 'POST',
            body: data,
            headers: authHeadersFormData()
        }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sites'] })
    });
}

export function useDeleteSite() {
    const queryClient = useQueryClient();
    return useMutation<void, Error, number>({
        mutationFn: (uniqueSiteId) => apiFetch<void>(`api/SiteInfo/Delete?id=${uniqueSiteId}`, {
            method: 'DELETE',
            headers: authHeaders()
        }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sites'] })
    });
}
